import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { API_BASE_URL, getHeaders } from '../utils/api';
import { readAdminApiErrorMessage } from '../utils/adminUi';
import { ApiEndpoints } from '../utils/ApiEndpoints';
import { buildSitePayload, normalizeDomain, getDomainConfigurations, readSitePolicyError, canEnableDomainCapability, enforceCapabilityDependencies } from '../utils/siteDomainConfiguration';
import { getStorageStatus, provisionDefaultStorage, provisionSiteContainer, setSiteLegalHold, deleteSiteStorage } from '../services/storage.service';
import { getReplayPolicy, updateReplayPolicy, purgeSiteTelemetry } from '../services/heatmap.service';
import { getEntitlements } from '../services/metering.service';
import DomainCapabilityControls from './shared/DomainCapabilityControls';
import DomainPolicyRecovery from './shared/DomainPolicyRecovery';
import SiteDomainConfigurationState from './shared/SiteDomainConfigurationState';
import { showToast } from '../utils/toast';
import SearchableDropdown from './shared/SearchableDropdown';
import { trackEvent } from '../services/applicationInsights';
import {
  FaGlobe, FaPlus, FaSearch, FaEdit, FaTrash, FaExclamationTriangle,
  FaBuilding, FaSyncAlt, FaCircle, FaToggleOn, FaToggleOff,
  FaQuestionCircle, FaTimes, FaThLarge, FaChevronLeft,
  FaChevronRight, FaCog, FaCheckCircle, FaBan, FaChevronDown
} from "react-icons/fa";

export default function SiteCollections({ token, roles, orgId, orgContextResolved = true, initialOrganizationId = '', onNavigate }) {
  // Data State
  const [siteCollections, setSiteCollections] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);

  // UI & UX State
  const tableRef = useRef(null);
  const formRef = useRef(null);

  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const [form, setForm] = useState({
    name: '',
    description: '',
    domainNames: [],
    domainConfigurations: [],
    isActive: true,
    organizationId: '',
    previewBaseUrl: ''
  });

  const [domainToAdd, setDomainToAdd] = useState('');

  const [selectedOrgId, setSelectedOrgId] = useState(initialOrganizationId);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingOrgs, setFetchingOrgs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [blockingState, setBlockingState] = useState(null);
  const [policyState, setPolicyState] = useState(null);
  const [policyRefresh, setPolicyRefresh] = useState(0);
  const [saveError, setSaveError] = useState('');
  const [uncertainCreate, setUncertainCreate] = useState(false);
  const [savedDomainConfigurations, setSavedDomainConfigurations] = useState([]);
  const [pendingOperation, setPendingOperation] = useState(null);
  const [inspectedSite, setInspectedSite] = useState(null);
  const [siteContainers, setSiteContainers] = useState(() => {
    try {
      const stored = localStorage.getItem('iap_site_containers');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [provisioningContainerId, setProvisioningContainerId] = useState(null);
  const [togglingLegalHoldId, setTogglingLegalHoldId] = useState(null);
  const [deletingStorageId, setDeletingStorageId] = useState(null);
  const [purgingTelemetryId, setPurgingTelemetryId] = useState(null);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgeOptions, setPurgeOptions] = useState({
    includeSnapshots: true,
    includeSettings: false
  });
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [siteReplayPolicies, setSiteReplayPolicies] = useState({});

  useEffect(() => {
    try {
      localStorage.setItem('iap_site_containers', JSON.stringify(siteContainers));
    } catch {
      // Ignore storage quota or disabled storage in private browsing
    }
  }, [siteContainers]);

  const isContainerReady = useCallback((siteId) => {
    if (!siteId) return true;
    if (siteContainers[siteId]?.status === 'ready') return true;
    const policy = siteReplayPolicies[siteId];
    if (policy && (policy.sessionReplayEffective || (policy.sessionReplayBlockedReason && policy.sessionReplayBlockedReason !== 'storage_not_ready'))) {
      return true;
    }
    return false;
  }, [siteContainers, siteReplayPolicies]);

  const handleProvisionContainer = async (siteId, domainNames, targetOrgId) => {
    setProvisioningContainerId(siteId);
    try {
      const targetOrg = targetOrgId || getTargetOrganizationId();
      try {
        const storageStatus = await getStorageStatus(targetOrg);
        if (storageStatus?.status === 'not_provisioned') {
          await provisionDefaultStorage(targetOrg);
        }
      } catch (storageCheckErr) {
        console.warn('Storage preflight check/provision failed:', storageCheckErr?.message);
      }
      const origins = (domainNames || []).map(d => (d.startsWith('http') ? d : `https://${d}`));
      const result = await provisionSiteContainer(targetOrg, siteId, origins);
      setSiteContainers(prev => ({ ...prev, [siteId]: result }));
      showToast.success(`Storage container ${result.containerName || ''} provisioned successfully.`);
    } catch (err) {
      showToast.error(err.message || 'Failed to provision site storage container.');
    } finally {
      setProvisioningContainerId(null);
    }
  };

  const handleToggleLegalHold = async (siteId, nextActive, targetOrgId) => {
    setTogglingLegalHoldId(siteId);
    try {
      const org = targetOrgId || getTargetOrganizationId();
      const result = await setSiteLegalHold(org, siteId, nextActive);
      setSiteContainers(prev => ({
        ...prev,
        [siteId]: {
          ...(prev[siteId] || {}),
          legalHoldActive: result.legalHoldActive,
          legalHoldReason: result.legalHoldReason
        }
      }));
      showToast.success(nextActive ? 'Legal hold activated on site collection.' : 'Legal hold removed from site collection.');
    } catch (err) {
      showToast.error(err.message || 'Failed to update legal hold status.');
    } finally {
      setTogglingLegalHoldId(null);
    }
  };

  const handleDeleteSiteStorage = async (siteId, targetOrgId) => {
    const isHoldActive = siteContainers[siteId]?.legalHoldActive;
    if (isHoldActive) {
      showToast.warning('Cannot delete storage container: Legal hold is active on this site collection.');
      return;
    }

    if (!window.confirm('Are you sure you want to clean up all site behavioural data and delete this storage container? This action is permanent.')) {
      return;
    }

    setDeletingStorageId(siteId);
    try {
      const org = targetOrgId || getTargetOrganizationId();
      const result = await deleteSiteStorage(org, siteId);
      if (result?.status === 'hold_blocked') {
        showToast.warning(result.message || 'Cannot delete storage container: Legal hold is active.');
      } else {
        setSiteContainers(prev => {
          const updated = { ...prev };
          delete updated[siteId];
          return updated;
        });
        showToast.success('Site storage container and behavioural data deleted successfully.');
      }
    } catch (err) {
      showToast.error(err.message || 'Failed to delete site storage container.');
    } finally {
      setDeletingStorageId(null);
    }
  };

  const handleOpenPurgeModal = (siteId, targetOrgId) => {
    const org = targetOrgId || getTargetOrganizationId();
    setPurgeTarget({ siteId, orgId: org });
    setPurgeOptions({ includeSnapshots: true, includeSettings: false });
    setShowPurgeModal(true);
  };

  const handleClosePurgeModal = () => {
    if (purgingTelemetryId) return;
    setShowPurgeModal(false);
    setPurgeTarget(null);
  };

  const handleExecutePurge = async () => {
    if (!purgeTarget?.siteId) return;
    const { siteId, orgId: targetOrg } = purgeTarget;

    const isHoldActive = siteContainers[siteId]?.legalHoldActive;
    if (isHoldActive) {
      showToast.warning('Cannot purge telemetry data: Legal hold is active on this site collection.');
      return;
    }

    setPurgingTelemetryId(siteId);
    try {
      const result = await purgeSiteTelemetry(targetOrg, siteId, {
        includeSnapshots: purgeOptions.includeSnapshots,
        includeSettings: purgeOptions.includeSettings
      });

      const deletedCount = result?.deletedCount ?? 0;
      showToast.success(
        result?.message || `Telemetry data successfully purged (${deletedCount} record${deletedCount === 1 ? '' : 's'} removed).`
      );
      setShowPurgeModal(false);
      setPurgeTarget(null);
      setPolicyRefresh(v => v + 1);
      fetchSiteCollections(targetOrg);
    } catch (err) {
      if (err?.status === 409 || err?.message?.toLowerCase().includes('legal hold')) {
        showToast.warning('Cannot purge telemetry data: Legal hold is active on this site collection or organization.');
      } else if (err?.status === 403) {
        showToast.error('You do not have permission to purge telemetry data for this site collection.');
      } else {
        showToast.error(err?.message || 'Failed to purge telemetry data.');
      }
    } finally {
      setPurgingTelemetryId(null);
    }
  };

  const fetchSitesAbortRef = useRef(null);
  const fetchUsersAbortRef = useRef(null);
  const fetchOrgsAbortRef = useRef(null);

  const roleList = Array.isArray(roles) ? roles : [];
  const normalizedRoles = roleList.map(r => String(r).toLowerCase());
  const isSystemAdmin = normalizedRoles.includes('systemadministrator');
  const isOrganizationAdmin = normalizedRoles.includes('organizationadministrator');
  const isContentCreator = normalizedRoles.includes('contentcreator');
  const isSiteCollectionOwner = normalizedRoles.includes('sitecollectionowner');
  const isRegularUser = normalizedRoles.includes('regularuser');
  const canViewCollections = isSystemAdmin || isOrganizationAdmin || isContentCreator || isSiteCollectionOwner || isRegularUser;
  const canCreateOrUpdateCollections = isSystemAdmin || isOrganizationAdmin || isContentCreator;
  const canDeleteCollections = isSystemAdmin || isOrganizationAdmin;
  const organizationSelection = isSystemAdmin ? selectedOrgId : orgId;

  useEffect(() => {
    return () => {
      fetchSitesAbortRef.current?.abort();
      fetchUsersAbortRef.current?.abort();
      fetchOrgsAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!isSystemAdmin && orgId && selectedOrgId !== orgId) {
      setSelectedOrgId(orgId);
    }
  }, [isSystemAdmin, orgId, selectedOrgId]);

  const resetForm = useCallback((organizationValue = '') => {
    setForm({
      name: '',
      description: '',
      domainNames: [],
      domainConfigurations: [],
      isActive: true,
      organizationId: organizationValue,
      previewBaseUrl: ''
    });
    setDomainToAdd('');
    setSaveError('');
    setUncertainCreate(false);
    setSavedDomainConfigurations([]);
  }, []);

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingId(null);
    setInspectedSite(null);
    resetForm(isSystemAdmin ? selectedOrgId : orgId);
  }, [isSystemAdmin, selectedOrgId, orgId, resetForm]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showForm) {
        handleCloseForm();
      }
    };
    if (showForm) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showForm, handleCloseForm]);

  const getTargetOrganizationId = useCallback((organizationOverride) => {
    if (isSystemAdmin) {
      return organizationOverride || organizationSelection || '';
    }

    return orgId || '';
  }, [isSystemAdmin, orgId, organizationSelection]);

  const policyOrganizationId = getTargetOrganizationId(showForm ? form.organizationId : undefined);
  const currentPolicy = policyState?.organizationId === policyOrganizationId ? policyState : null;
  const stateOrganizationId = showForm ? policyOrganizationId :
    inspectedSite?.organizationId === getTargetOrganizationId() ? inspectedSite.organizationId : '';
  const stateSiteId = showForm ? editingId : stateOrganizationId ? inspectedSite?.id : null;
  const handleStateOperation = useCallback(operation => {
    setPendingOperation(previous => previous?.operationId === operation.operationId ? previous : {
      organizationId: stateOrganizationId,
      operationId: operation.operationId,
      isBlockingOperation: true,
      resumeEditor: false
    });
  }, [stateOrganizationId]);
  useEffect(() => {
    if (!showForm || !policyOrganizationId) return undefined;
    let cancelled = false;
    setPolicyState(null);
    getEntitlements(policyOrganizationId)
      .then(entitlements => {
        if (!cancelled) setPolicyState(previous => ({
          ...previous, organizationId: policyOrganizationId, entitlements, loaded: true
        }));
      })
      .catch(() => {
        if (!cancelled) setPolicyState(previous => ({
          ...previous, organizationId: policyOrganizationId, entitlements: null, loaded: true
        }));
      });
    return () => { cancelled = true; };
  }, [showForm, policyOrganizationId, policyRefresh, token]);

  useEffect(() => {
    const enabled = currentPolicy?.entitlements?.enabledFeatures || [];
    const hasReplayEntitlement = Boolean(
      enabled.includes('session_replay') ||
      (enabled.includes('customer_analytics') && enabled.includes('heat_map'))
    );
    if (!showForm || !editingId || !policyOrganizationId || !hasReplayEntitlement) return undefined;
    let cancelled = false;
    getReplayPolicy(policyOrganizationId, editingId)
      .then(policy => {
        if (!cancelled && policy) {
          setSiteReplayPolicies(previous => ({ ...previous, [editingId]: policy }));
          if (policy.sessionReplayRequested !== undefined) {
            setForm(previous => {
              if (!Array.isArray(previous.domainConfigurations)) return previous;
              const currentConfigs = previous.domainConfigurations;
              const hasExplicit = currentConfigs.some(c => c.session_replay !== undefined);
              if (hasExplicit) return previous;
              return {
                ...previous,
                domainConfigurations: getDomainConfigurations(previous, policy)
              };
            });
            setSavedDomainConfigurations(previous => {
              const current = Array.isArray(previous) ? previous : [];
              const hasExplicit = current.some(c => c.session_replay !== undefined);
              if (hasExplicit) return previous;
              return getDomainConfigurations({ domainConfigurations: current, domainNames: current.map(p => p.domain) }, policy);
            });
          }
          if (policy.sessionReplayEffective || (policy.sessionReplayBlockedReason && policy.sessionReplayBlockedReason !== 'storage_not_ready')) {
            setSiteContainers(prev => {
              if (prev[editingId]?.status === 'ready') return prev;
              return {
                ...prev,
                [editingId]: {
                  ...(prev[editingId] || {}),
                  status: 'ready',
                  containerName: prev[editingId]?.containerName || `c-${editingId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 12)}`
                }
              };
            });
          }
        }
      })
      .catch(() => {
        // Replay policy uninitialized or absent
      });
    return () => { cancelled = true; };
  }, [showForm, editingId, policyOrganizationId, policyRefresh, currentPolicy?.entitlements]);

  const getBlockingTitle = useCallback((status) => {
    if (status === 401) return 'Session expired';
    if (status === 403) return 'Access denied';
    if (status === 404) return 'Site collection not found';
    if (status === 400) return 'Invalid organization context';
    return 'Site collections unavailable';
  }, []);

  // Data Fetching
  const fetchOrganizations = useCallback(async () => {
    if (!isSystemAdmin) return;

    fetchOrgsAbortRef.current?.abort();
    const controller = new AbortController();
    fetchOrgsAbortRef.current = controller;

    setFetchingOrgs(true);
    try {
      const res = await fetch(`${API_BASE_URL}/organizations`, {
        headers: getHeaders(token),
        signal: controller.signal
      });

      if (!res.ok) {
        throw new Error(await readAdminApiErrorMessage(res, {
          401: 'Your session is invalid or expired. Please sign in again.',
          403: 'You do not have permission to view organizations.',
          500: 'Failed to fetch organizations.',
          default: 'Failed to fetch organizations.'
        }));
      }

      const data = await res.json().catch(() => []);
      const organizationItems = Array.isArray(data) ? data : [];
      setOrganizations(organizationItems);

      setSelectedOrgId((prev) => {
        if (prev && !organizationItems.some((o) => o.id === prev)) {
          return '';
        }
        return prev;
      });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      showToast.error(err.message || 'Failed to fetch organizations.');
      setOrganizations([]);
    } finally {
      if (fetchOrgsAbortRef.current === controller) {
        setFetchingOrgs(false);
      }
    }
  }, [isSystemAdmin, token]);

  const fetchSiteCollections = useCallback(async (orgToFetch) => {
    if (!canViewCollections) {
      setBlockingState({
        title: 'Access denied',
        message: 'You do not have permission to view site collections.'
      });
      return;
    }

    const targetOrgId = getTargetOrganizationId(orgToFetch);
    if (isSystemAdmin && !targetOrgId) {
      fetchSitesAbortRef.current?.abort();
      setSiteCollections([]);
      setBlockingState({
        title: 'Select an organization to begin',
        message: 'Please select an organization to view site collections.'
      });
      return;
    }

    if (!isSystemAdmin && !targetOrgId && !orgContextResolved) {
      fetchSitesAbortRef.current?.abort();
      setSiteCollections([]);
      setBlockingState(null);
      return;
    }

    if (!targetOrgId && !isSystemAdmin) {
      fetchSitesAbortRef.current?.abort();
      setSiteCollections([]);
      setBlockingState({
        title: 'Invalid organization context',
        message: 'Invalid organization context'
      });
      return;
    }

    fetchSitesAbortRef.current?.abort();
    const controller = new AbortController();
    fetchSitesAbortRef.current = controller;

    setLoading(true);
    setBlockingState(null);
    try {
      const url = isSystemAdmin
        ? `${API_BASE_URL}${ApiEndpoints.siteCollections}?organizationId=${encodeURIComponent(targetOrgId)}`
        : `${API_BASE_URL}${ApiEndpoints.siteCollections}`;
      const res = await fetch(url, {
        headers: getHeaders(token),
        signal: controller.signal
      });

      if (!res.ok) {
        const message = await readAdminApiErrorMessage(res, {
          400: 'Invalid organization context',
          401: 'Your session is invalid or expired. Please sign in again.',
          403: 'You do not have permission to access site collections from other organizations',
          404: 'Site collection not found.',
          500: 'An error occurred while retrieving site collections',
          default: 'Failed to load site collections.'
        });

        setSiteCollections([]);
        setBlockingState({
          title: getBlockingTitle(res.status),
          message
        });
        return;
      }

      const data = await res.json().catch(() => []);
      const sites = Array.isArray(data) ? data : [];
      setSiteCollections(sites);
      setSiteContainers(prev => {
        const next = { ...prev };
        let changed = false;
        sites.forEach(s => {
          const cName = s.containerName || s.storageContainer?.containerName;
          const cStatus = s.containerStatus || s.storageContainer?.status || (cName ? 'ready' : undefined);
          if (cName && (!next[s.id] || next[s.id].status !== cStatus)) {
            next[s.id] = {
              ...(next[s.id] || {}),
              containerName: cName,
              status: cStatus || 'ready',
              legalHoldActive: Boolean(s.legalHoldActive ?? s.storageContainer?.legalHoldActive ?? next[s.id]?.legalHoldActive)
            };
            changed = true;
          }
        });
        return changed ? next : prev;
      });
      setBlockingState(null);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setSiteCollections([]);
      setBlockingState({
        title: 'Site collections unavailable',
        message: 'An error occurred while retrieving site collections'
      });
    } finally {
      if (fetchSitesAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [canViewCollections, getBlockingTitle, getTargetOrganizationId, isSystemAdmin, orgContextResolved, token]);

  const fetchUsers = useCallback(async (orgIdForUsers) => {
    if (!orgIdForUsers) {
      fetchUsersAbortRef.current?.abort();
      setUsers([]);
      return;
    }

    fetchUsersAbortRef.current?.abort();
    const controller = new AbortController();
    fetchUsersAbortRef.current = controller;

    try {
      const res = await fetch(`${API_BASE_URL}/users/organizations/${orgIdForUsers}`, {
        headers: getHeaders(token),
        signal: controller.signal
      });
      if (!res.ok) {
        setUsers([]);
        return;
      }

      const data = await res.json().catch(() => []);
      setUsers((Array.isArray(data) ? data : []).filter((u) => u.approvalStatus === 'Approved'));
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setUsers([]);
    }
  }, [token]);

  useEffect(() => { fetchOrganizations(); }, [fetchOrganizations]);

  useEffect(() => {
    if (!canViewCollections) {
      setBlockingState({
        title: 'Access denied',
        message: 'You do not have permission to view site collections.'
      });
      return;
    }

    const target = getTargetOrganizationId();

    if (!isSystemAdmin && !target && !orgContextResolved) {
      setBlockingState(null);
      setSiteCollections([]);
      setUsers([]);
      return;
    }

    fetchSiteCollections(target);

    if (target) {
      fetchUsers(target);
    } else {
      setUsers([]);
    }
  }, [canViewCollections, fetchSiteCollections, fetchUsers, getTargetOrganizationId, isSystemAdmin, orgContextResolved]);

  const handleKpiClick = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const executeAction = async () => {
    if (!confirmation || pendingOperation || submitting) return;
    const { id, type, data } = confirmation;
    setSubmitting(true);
    setConfirmation(null);
    try {
      if (type === 'DELETE') {
        const res = await fetch(`${API_BASE_URL}${ApiEndpoints.siteCollection(id)}`, {
          method: 'DELETE',
          headers: getHeaders(token)
        });

        if (!res.ok) {
          throw new Error(await readAdminApiErrorMessage(res, {
            401: 'Your session is invalid or expired. Please sign in again.',
            403: 'You do not have permission to delete this site collection.',
            404: `Site collection with ID ${id} not found.`,
            409: 'Site collection cannot be deleted while flows, content, UX experiences, or visibilities exist for this site collection.',
            500: 'Site collection deletion failed. Please try again.',
            default: 'Site collection deletion failed. Please try again.'
          }));
        }

        showToast.success('Site collection deleted successfully.');
        if (inspectedSite?.id === id) setInspectedSite(null);
      } else if (type === 'TOGGLE_STATUS') {
        const newStatus = !data.isActive;
        const res = await fetch(`${API_BASE_URL}${ApiEndpoints.siteCollection(id)}`, {
          method: 'PUT',
          headers: { ...getHeaders(token), 'Content-Type': 'application/json' },
          body: JSON.stringify(buildSitePayload({ ...data, id, isActive: newStatus },
            data.organizationId || getTargetOrganizationId()))
        });

        if (!res.ok || res.status === 202) throw await readSitePolicyError(res,
          res.status === 404 ? `Site collection with ID ${id} not found.` :
            res.status === 403 ? 'You do not have permission to update this site collection.' : undefined);

        showToast.success(`Site collection ${newStatus ? 'activated' : 'deactivated'} successfully.`);
        setInspectedSite({ id, organizationId: data.organizationId || getTargetOrganizationId() });
        setPolicyRefresh(value => value + 1);
      }
      fetchSiteCollections(getTargetOrganizationId());
    } catch (err) {
      showToast.error(err.message || 'Operation failed.');
      if (err.operationId) setPendingOperation({
        organizationId: data?.organizationId || getTargetOrganizationId(),
        operationId: err.operationId, resumeEditor: false,
        isBlockingOperation: err.isBlockingOperation,
        blockedAction: err.isBlockingOperation === true ? { id, type, data } : null
      });
      fetchSiteCollections(getTargetOrganizationId());
      setPolicyRefresh(value => value + 1);
    } finally { setSubmitting(false); }
  };

  const addDomain = () => {
    const normalized = normalizeDomain(domainToAdd);
    if (!normalized) return;

    const existing = (form.domainNames || []).map((d) => normalizeDomain(d));
    if (existing.includes(normalized)) {
      showToast.error('Domain already added.');
      return;
    }

    setForm((prev) => ({ ...prev, domainNames: [...(prev.domainNames || []), normalized] }));
    setDomainToAdd('');
  };

  const removeDomain = (domain) => {
    const normalized = normalizeDomain(domain);
    setForm((prev) => ({
      ...prev,
      domainNames: (prev.domainNames || []).filter((d) => normalizeDomain(d) !== normalized),
      ...(Array.isArray(prev.domainConfigurations) ? {
        domainConfigurations: prev.domainConfigurations.filter(item => normalizeDomain(item.domain) !== normalized)
      } : {})
    }));
  };

  const changeDomainCapability = (domain, flag, checked) => {
    setForm(previous => ({
      ...previous,
      domainConfigurations: getDomainConfigurations(previous, editingId ? siteReplayPolicies[editingId] : null).map(configuration =>
        configuration.domain !== domain ? configuration : enforceCapabilityDependencies(configuration, flag, checked)
      )
    }));
  };

  const validatePreviewBaseUrl = useCallback((previewBaseUrl, domainNames) => {
    const trimmedPreviewBaseUrl = (previewBaseUrl || '').trim();
    if (!trimmedPreviewBaseUrl) {
      return '';
    }

    let parsedUrl;
    try {
      parsedUrl = new URL(trimmedPreviewBaseUrl);
    } catch {
      return 'PreviewBaseUrl must be an absolute URL with http or https scheme.';
    }

    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return 'PreviewBaseUrl must be an absolute URL with http or https scheme.';
    }

    const normalizedHost = normalizeDomain(parsedUrl.host);
    const normalizedHostname = normalizeDomain(parsedUrl.hostname);
    const normalizedDomains = Array.from(new Set((domainNames || []).map(normalizeDomain).filter(Boolean)));

    if (!normalizedDomains.includes(normalizedHost) && !normalizedDomains.includes(normalizedHostname)) {
      return 'PreviewBaseUrl host must exist in DomainNames.';
    }

    return '';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uncertainCreate || pendingOperation) return;

    if (!canCreateOrUpdateCollections) {
      showToast.error('You do not have permission to create or update site collections.');
      return;
    }

    if (!form.domainNames || form.domainNames.length === 0) {
      showToast.error('Please add at least one domain name.');
      return;
    }

    const targetOrganizationId = getTargetOrganizationId(form.organizationId);
    if (!targetOrganizationId && isSystemAdmin) {
      showToast.error('Organization ID is required for system administrators');
      return;
    }

    const previewBaseUrlError = validatePreviewBaseUrl(form.previewBaseUrl, form.domainNames);
    if (previewBaseUrlError) {
      showToast.error(previewBaseUrlError);
      return;
    }

    const hasUnlicensedAddition = getDomainConfigurations(form).some(configuration =>
      ['consumerExperience', 'heat_map', 'customer_analytics'].some(flag =>
        configuration[flag] && !savedDomainConfigurations.find(item => item.domain === configuration.domain)?.[flag] &&
        (!currentPolicy?.loaded || !canEnableDomainCapability(flag, configuration, currentPolicy.entitlements))));
    if (hasUnlicensedAddition) {
      setSaveError('New enablement requires current organization entitlements and origin policy. Your draft is retained; you can turn choices off.');
      return;
    }

    setSubmitting(true);
    setSaveError('');
    try {
      const url = `${API_BASE_URL}${editingId ? ApiEndpoints.siteCollection(editingId) : ApiEndpoints.siteCollections}`;
      const finalForm = buildSitePayload(form, targetOrganizationId);

      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...getHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify(finalForm)
      });

      if (!res.ok || res.status === 202) throw await readSitePolicyError(res,
        res.status === 404 ? `Site collection with ID ${editingId} not found.` :
          res.status === 403 ? `You do not have permission to ${editingId ? 'update' : 'create'} this site collection.` : undefined);

      let savedSiteId = editingId;
      if (!savedSiteId) {
        const createdSite = await res.json();
        if (!createdSite?.id || createdSite.organizationId !== targetOrganizationId) {
          throw new Error('The create response did not identify a site in the selected organization. Do not create a duplicate; your draft is retained.');
        }
        savedSiteId = createdSite.id;
      }

      // Sync Replay Policy if session_replay is requested or was previously active
      const domainConfigs = getDomainConfigurations(form);
      const isReplayRequested = domainConfigs.some(c => Boolean(c.session_replay));
      const wasReplayActive = (savedDomainConfigurations || []).some(c => Boolean(c.session_replay));
      const isHeatmapRequested = domainConfigs.some(c => Boolean(c.heat_map));
      const isAnalyticsRequested = domainConfigs.some(c => Boolean(c.customer_analytics));

      if (isReplayRequested || wasReplayActive) {
        try {
          if (isReplayRequested) {
            try {
              const storageStatus = await getStorageStatus(targetOrganizationId);
              if (storageStatus?.status !== 'ready') {
                await provisionDefaultStorage(targetOrganizationId);
              }
            } catch (storageErr) {
              console.warn('Storage preflight check/provision failed:', storageErr?.message);
            }
          }

          let updatedPolicy = await updateReplayPolicy(targetOrganizationId, savedSiteId, {
            heatMapEnabled: isHeatmapRequested,
            customerAnalyticsEnabled: isAnalyticsRequested,
            sessionReplayRequested: isReplayRequested,
            retentionDays: 14,
            samplingRatePercent: 100
          });

          if (isReplayRequested && updatedPolicy?.sessionReplayEffective === false && updatedPolicy?.sessionReplayBlockedReason === 'storage_not_ready') {
            try {
              await provisionDefaultStorage(targetOrganizationId);
              updatedPolicy = await updateReplayPolicy(targetOrganizationId, savedSiteId, {
                heatMapEnabled: isHeatmapRequested,
                customerAnalyticsEnabled: isAnalyticsRequested,
                sessionReplayRequested: isReplayRequested,
                retentionDays: 14,
                samplingRatePercent: 100
              });
            } catch (retryErr) {
              console.warn('Storage provision retry failed:', retryErr?.message);
            }
          }

          setSiteReplayPolicies(prev => ({ ...prev, [savedSiteId]: updatedPolicy }));

          if (isReplayRequested && (!updatedPolicy?.sessionReplayEffective || updatedPolicy?.sessionReplayBlockedReason)) {
            const reason = updatedPolicy?.sessionReplayBlockedReason === 'storage_not_ready'
              ? 'Organization storage is not ready'
              : updatedPolicy?.sessionReplayBlockedReason === 'requires_heatmap_and_customer_analytics'
                ? 'Requires basic heatmap and customer analytics'
                : updatedPolicy?.sessionReplayBlockedReason || 'Policy rejected';
            showToast.warning(`Site collection saved, but Session Replay is blocked: ${reason}.`);
          }
        } catch (policyErr) {
          console.warn('Replay policy sync error:', policyErr?.message);
          showToast.warning(`Site collection saved, but failed to sync Session Replay policy: ${policyErr?.message || 'Unknown error'}`);
        }
      }

      setInspectedSite(null);
      setPolicyRefresh(value => value + 1);
      showToast.success(editingId ? 'Site collection updated successfully.' : 'Site collection created successfully.');
      setShowForm(false);
      setEditingId(null);
      fetchSiteCollections(targetOrganizationId);
      resetForm(isSystemAdmin ? targetOrganizationId : orgId);
    } catch (err) {
      setSaveError(err.message || 'The server has not confirmed this save. Your draft is retained.');
      if (err.operationId) {
        setPendingOperation({
          organizationId: targetOrganizationId, operationId: err.operationId,
          isBlockingOperation: err.isBlockingOperation,
          resumeEditor: err.isBlockingOperation === false
        });
      } else if (!editingId && !err.requestNotStarted && (err.status === 202 || err.status >= 500 || !err.status ||
        /pending|reconcil/i.test(err.code || ''))) {
        setUncertainCreate(true);
      }
      showToast.error(err.message || 'Operation failed.');
      fetchSiteCollections(targetOrganizationId);
      setPolicyRefresh(value => value + 1);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCollections = useMemo(() => {
    const q = (searchTerm ?? '').toLowerCase().trim();
    const qId = q.replace(/^id[:\s#-]*/i, '');

    return siteCollections.filter((sc) => {
      const idStr = String(sc.id ?? '').toLowerCase();

      const matchesSearch =
        (sc.name ?? '').toLowerCase().includes(q) ||
        (sc.domainNames || []).some(d => (d ?? '').toString().toLowerCase().includes(q)) ||
        idStr.includes(qId);

      if (filterStatus === 'All') return matchesSearch;
      if (filterStatus === 'Active') return matchesSearch && !!sc.isActive;
      if (filterStatus === 'Inactive') return matchesSearch && !sc.isActive;
      return matchesSearch;
    });
  }, [siteCollections, searchTerm, filterStatus]);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCollections.slice(start, start + itemsPerPage);
  }, [filteredCollections, currentPage]);

  const stats = useMemo(() => ({
    total: siteCollections.length,
    active: siteCollections.filter(s => s.isActive).length,
    inactive: siteCollections.filter(s => !s.isActive).length
  }), [siteCollections]);

  const getOwnerDetails = useCallback((id) => {
    const user = users.find(u => String(u.id) === String(id));
    return user || { firstName: 'Unknown', lastName: 'User', isActive: false };
  }, [users]);

  const hasInactiveOwners = useCallback((sc) => {
    if (!sc.owners || sc.owners.length === 0) return false;
    return sc.owners.some(id => !getOwnerDetails(id).isActive);
  }, [getOwnerDetails]);

  return (
    <div style={wrapperStyle}>
      <div style={containerStyle}>

        <header style={headerStyle}>
          <div style={headerContentStyle}>
            <div style={headerBadgeStyle}><FaGlobe size={22} /></div>
            <div>
              <h1 style={titleStyle}>Site Collections</h1>
              <p style={subtitleStyle}>Collection of {stats.total} digital workspaces</p>
            </div>
          </div>
        </header>

        {isSystemAdmin && (
          <div style={filtersCardStyle}>
            <div style={cardHeaderStyle}>
              <FaBuilding color="#6366f1" size={14} />
              <span style={cardTitleStyle}>Organization Context</span>
            </div>
            <div style={filtersGridStyle}>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Select Organization</label>
                <SearchableDropdown
                  options={organizations}
                  selectedValue={selectedOrgId}
                  onSelect={(value) => {
                    setSelectedOrgId(value);
                    if (value) {
                      trackEvent('Dropdown Selected', {
                        section: 'sitecollections',
                        organizationId: value
                      });
                    }
                  }}
                  placeholder="Choose Organization"
                  displayKey="name"
                  valueKey="id"
                  isLoading={fetchingOrgs}
                  disabled={fetchingOrgs || Boolean(pendingOperation) || submitting}
                  styles={{
                    container: dropdownContainerStyle,
                    trigger: dropdownTriggerStyle,
                    openTrigger: { borderColor: '#6366f1' },
                    triggerText: dropdownTriggerTextStyle,
                    indicator: { color: '#94a3b8' },
                    menu: dropdownMenuStyle,
                    searchContainer: searchBoxContainerStyle,
                    searchIcon: searchIconStyle,
                    searchInput: dropdownSearchInputStyle,
                    list: dropdownListStyle,
                    option: dropdownOptionStyle,
                    optionText: optionTextStyle,
                    optionName: optionNameStyle,
                    optionId: optionIdStyle,
                    emptyOption: emptyOptionStyle
                  }}
                />
              </div>
              <button style={primaryButtonStyle} onClick={() => fetchSiteCollections(selectedOrgId)} disabled={loading || !selectedOrgId || fetchingOrgs}>
                <FaSyncAlt className={loading ? 'spin' : ''} /> {loading ? 'Syncing...' : 'Sync Collections'}
              </button>
            </div>
          </div>
        )}

        {/* KPI ROW */}
        <div style={kpiRowStyle}>
          <div className="kpi-card" style={filterStatus === 'All' ? kpiCardActiveAll : kpiCardWhite} onClick={() => handleKpiClick('All')}>
            <div style={kpiHeader}><div style={{ ...kpiIcon, color: '#6366f1', background: '#f5f3ff' }}><FaThLarge /></div><span style={kpiLabel}>Total Collections</span></div>
            <div style={kpiValue}>{stats.total}</div>
            <div style={kpiMeta}>Registry volume</div>
          </div>
          <div className="kpi-card" style={filterStatus === 'Active' ? kpiCardActiveActive : kpiCardWhite} onClick={() => handleKpiClick('Active')}>
            <div style={kpiHeader}><div style={{ ...kpiIcon, background: '#f0fdf4', color: '#10b981' }}><FaCheckCircle /></div><span style={kpiLabel}>Active</span></div>
            <div style={{ ...kpiValue, color: '#10b981' }}>{stats.active}</div>
            <div style={kpiMeta}>Workspaces Online</div>
          </div>
          <div className="kpi-card" style={filterStatus === 'Inactive' ? kpiCardActiveInactive : kpiCardWhite} onClick={() => handleKpiClick('Inactive')}>
            <div style={kpiHeader}><div style={{ ...kpiIcon, background: '#fef2f2', color: '#ef4444' }}><FaBan /></div><span style={kpiLabel}>Inactive</span></div>
            <div style={{ ...kpiValue, color: '#ef4444' }}>{stats.inactive}</div>
            <div style={kpiMeta}>Archived assets</div>
          </div>
        </div>

        {/* SLIDE-OVER DRAWER OVERLAY */}
        {showForm && canCreateOrUpdateCollections && (
          <div
            style={drawerBackdropStyle}
            onClick={(e) => {
              if (e.target === e.currentTarget && !submitting) {
                handleCloseForm();
              }
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="drawer-heading"
          >
            <div ref={formRef} className="site-collection-drawer-panel" style={drawerPanelStyle}>
              {/* STICKY DRAWER HEADER */}
              <div style={drawerHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={drawerHeaderIconBadge}>
                    <FaCog color="#6366f1" size={18} />
                  </div>
                  <div>
                    <h2 id="drawer-heading" style={drawerHeadingStyle}>
                      {editingId ? 'Edit Workspace' : 'Configure New Workspace'}
                    </h2>
                    <p style={drawerSubheadingStyle}>
                      {editingId ? 'Update domain configuration, preview settings, and analytics capabilities' : 'Set up a new workspace with targeted domains and capabilities'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCloseForm}
                  disabled={submitting}
                  aria-label="Close"
                  style={drawerCloseBtnStyle}
                  title="Close (Esc)"
                >
                  <FaTimes size={16} />
                </button>
              </div>

              {/* FORM WRAPPER */}
              <form
                onSubmit={handleSubmit}
                style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
              >
                {/* SCROLLABLE BODY */}
                <div style={drawerBodyStyle}>
                  <div className="site-collection-form-grid" style={drawerFormGridStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>Display Name *</label>
                        <input
                          style={selectStyle}
                          value={form.name}
                          onChange={e => setForm({ ...form, name: e.target.value })}
                          required
                          placeholder="e.g. Sales Portal"
                        />
                      </div>

                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>Preview Base URL</label>
                        <input
                          style={selectStyle}
                          value={form.previewBaseUrl || ''}
                          onChange={(e) => setForm({ ...form, previewBaseUrl: e.target.value })}
                          placeholder="e.g. https://preview.mycompany.com"
                        />
                      </div>

                      {/* Domain Names */}
                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>Domain Names * (At least 1 required)</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input
                            style={{ ...selectStyle, flex: 1 }}
                            value={domainToAdd}
                            onChange={(e) => setDomainToAdd(e.target.value)}
                            placeholder="e.g. sales.org.com"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addDomain();
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={addDomain}
                            style={primaryButtonStyle}
                            disabled={!normalizeDomain(domainToAdd)}
                          >
                            <FaPlus /> Add
                          </button>
                        </div>

                        {(form.domainNames || []).length === 0 && (
                          <div style={{
                            padding: '8px 12px',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: 8,
                            color: '#dc2626',
                            fontSize: 12,
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                          }}>
                            <FaExclamationTriangle size={12} />
                            Please add at least one domain name
                          </div>
                        )}

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, minHeight: (form.domainNames || []).length === 0 ? 30 : 'auto' }}>
                          {Array.from(new Set((form.domainNames || []).map(normalizeDomain))).map((d) => (
                            <span key={d} style={domainBadgeStyle}>
                              {d}
                              <FaTimes
                                style={{ marginLeft: 8, cursor: 'pointer', opacity: 0.6 }}
                                onClick={() => removeDomain(d)}
                              />
                            </span>
                          ))}
                        </div>
                      </div>

                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>Description *</label>
                        <textarea
                          style={{ ...selectStyle, minHeight: 80, resize: 'none' }}
                          value={form.description}
                          onChange={e => setForm({ ...form, description: e.target.value })}
                          required
                          placeholder="Purpose of this workspace..."
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>Organization</label>
                        <input
                          style={{ ...selectStyle, background: '#f8fafc', color: '#64748b' }}
                          value={isSystemAdmin ? (organizations.find((organization) => String(organization.id) === String(getTargetOrganizationId(form.organizationId)))?.name || 'Selected via organization context') : 'Current signed-in organization'}
                          readOnly
                        />
                      </div>

                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>Notes</label>
                        <div style={{ ...selectStyle, minHeight: 120, background: '#f8fafc', color: '#64748b', lineHeight: 1.6 }}>
                          Site collection ownership is role-assignment-based and is managed outside this create or update form. Use the user-management workflow to assign or remove scoped site collection ownership.
                        </div>
                      </div>
                    </div>
                  </div>

                  {editingId && (
                    <div style={{ padding: '14px 18px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Site Storage & Governance (Container, Legal Hold & Lifecycle)</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                            Container readiness: <span style={{ fontWeight: 600, color: isContainerReady(editingId) ? '#10b981' : '#f59e0b' }}>
                              {isContainerReady(editingId) ? 'READY' : (siteContainers[editingId]?.status?.toUpperCase() || 'NOT PROVISIONED')}
                            </span>
                            {siteContainers[editingId]?.containerName && ` (${siteContainers[editingId].containerName})`}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          {!isContainerReady(editingId) ? (
                            <button
                              type="button"
                              onClick={() => handleProvisionContainer(editingId, form.domainNames, policyOrganizationId)}
                              disabled={provisioningContainerId === editingId}
                              style={{ ...primaryButtonStyle, padding: '7px 14px', fontSize: 12 }}
                            >
                              {provisioningContainerId === editingId ? <FaSyncAlt className="spin" size={12} /> : 'Provision Storage Container'}
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                data-testid="toggle-legal-hold-btn"
                                onClick={() => handleToggleLegalHold(editingId, !siteContainers[editingId]?.legalHoldActive, policyOrganizationId)}
                                disabled={togglingLegalHoldId === editingId}
                                style={{
                                  padding: '7px 12px',
                                  fontSize: 12,
                                  borderRadius: 6,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  border: '1px solid',
                                  borderColor: siteContainers[editingId]?.legalHoldActive ? '#f59e0b' : '#cbd5e1',
                                  background: siteContainers[editingId]?.legalHoldActive ? '#fffbeb' : '#ffffff',
                                  color: siteContainers[editingId]?.legalHoldActive ? '#b45309' : '#475569',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}
                              >
                                {togglingLegalHoldId === editingId ? (
                                  <FaSyncAlt className="spin" size={12} />
                                ) : (
                                  <>
                                    {siteContainers[editingId]?.legalHoldActive ? <FaToggleOn size={16} /> : <FaToggleOff size={16} />}
                                    {siteContainers[editingId]?.legalHoldActive ? 'Legal Hold Active' : 'Enable Legal Hold'}
                                  </>
                                )}
                              </button>

                              <button
                                type="button"
                                data-testid="delete-site-storage-btn"
                                onClick={() => handleDeleteSiteStorage(editingId, policyOrganizationId)}
                                disabled={deletingStorageId === editingId}
                                style={{
                                  padding: '7px 12px',
                                  fontSize: 12,
                                  borderRadius: 6,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  border: '1px solid #fecaca',
                                  background: '#fef2f2',
                                  color: '#dc2626'
                                }}
                              >
                                {deletingStorageId === editingId ? <FaSyncAlt className="spin" size={12} /> : 'Delete Container'}
                              </button>
                            </>
                          )}

                          <button
                            type="button"
                            data-testid="purge-site-telemetry-btn"
                            onClick={() => handleOpenPurgeModal(editingId, policyOrganizationId)}
                            disabled={purgingTelemetryId === editingId}
                            style={{
                              padding: '7px 12px',
                              fontSize: 12,
                              borderRadius: 6,
                              fontWeight: 600,
                              cursor: 'pointer',
                              border: '1px solid #fed7aa',
                              background: '#fff7ed',
                              color: '#c2410c',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6
                            }}
                            title="Purge telemetry data and reset interaction metrics while preserving site and flows"
                          >
                            {purgingTelemetryId === editingId ? (
                              <FaSyncAlt className="spin" size={12} />
                            ) : (
                              <FaTrash size={12} />
                            )}
                            Purge Telemetry Data
                          </button>
                        </div>
                      </div>

                      {siteContainers[editingId]?.legalHoldActive && (
                        <div
                          data-testid="legal-hold-warning-banner"
                          style={{ padding: '8px 12px', background: '#fffbeb', borderRadius: 6, border: '1px solid #fde68a', fontSize: 12, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}
                        >
                          <FaExclamationTriangle size={14} />
                          <span><strong>Legal Hold Active:</strong> All behavioural data retention is locked. Storage container deletion is blocked until legal hold is disabled.</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <DomainCapabilityControls
                      organizationId={policyOrganizationId}
                      configurations={getDomainConfigurations(form, editingId ? siteReplayPolicies[editingId] : null)}
                      entitlements={currentPolicy?.entitlements}
                      loading={!currentPolicy?.loaded}
                      disabled={submitting || !canCreateOrUpdateCollections}
                      replayPolicy={editingId ? siteReplayPolicies[editingId] : null}
                      onChange={changeDomainCapability}
                      onProvisionStorage={editingId ? () => handleProvisionContainer(editingId, form.domainNames, policyOrganizationId) : undefined}
                    />
                    {saveError && <p role="alert" style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{saveError}</p>}
                    {uncertainCreate && <p role="alert" style={{ color: '#d97706', fontSize: 13, marginTop: 8 }}>Creation may have completed with capture pending, but no operation identifier was returned. Obtain the original operation identifier before recovery; do not create a duplicate. This draft remains here for reference.</p>}
                    <button type="button" onClick={() => {
                      setPolicyRefresh(value => value + 1);
                      fetchSiteCollections(policyOrganizationId);
                    }} style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: 12, cursor: 'pointer', padding: 0, marginTop: 8, textDecoration: 'underline' }}>
                      Reload server status (keep draft)
                    </button>
                  </div>

                  <SiteDomainConfigurationState
                    organizationId={stateOrganizationId}
                    siteCollectionId={stateSiteId}
                    token={token}
                    refreshKey={policyRefresh}
                    isSaving={submitting}
                    draftDomains={form.domainNames}
                    onOperation={handleStateOperation}
                    onNavigate={onNavigate}
                  />
                </div>

                {/* STICKY FOOTER */}
                <div style={drawerFooterStyle}>
                  <button
                    type="button"
                    disabled={Boolean(pendingOperation)}
                    onClick={handleCloseForm}
                    style={{ ...primaryButtonStyle, background: '#f1f5f9', color: '#64748b' }}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    style={primaryButtonStyle}
                    disabled={submitting || uncertainCreate || Boolean(pendingOperation) || (form.domainNames || []).length === 0}
                  >
                    {submitting ? 'Syncing...' : (editingId ? 'Update Collection' : 'Create Collection')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPurgeModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              zIndex: 1100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget && !purgingTelemetryId) {
                handleClosePurgeModal();
              }
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="purge-modal-title"
            data-testid="purge-telemetry-modal"
          >
            <div
              style={{
                width: '100%',
                maxWidth: 520,
                background: '#ffffff',
                borderRadius: 14,
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                zIndex: 1101
              }}
            >
              {/* MODAL HEADER */}
              <div
                style={{
                  padding: '18px 24px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#ffffff'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: '#ffedd5',
                      color: '#ea580c',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FaExclamationTriangle size={18} />
                  </div>
                  <div>
                    <h3
                      id="purge-modal-title"
                      style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}
                    >
                      Purge Telemetry & Reset Data
                    </h3>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0 0' }}>
                      Permanent telemetry reset for this site collection
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClosePurgeModal}
                  disabled={Boolean(purgingTelemetryId)}
                  style={drawerCloseBtnStyle}
                  title="Close"
                  aria-label="Close dialog"
                >
                  <FaTimes size={14} />
                </button>
              </div>

              {/* MODAL BODY */}
              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {siteContainers[purgeTarget?.siteId]?.legalHoldActive && (
                  <div
                    data-testid="purge-legal-hold-active-banner"
                    style={{
                      padding: '10px 14px',
                      background: '#fffbeb',
                      border: '1px solid #fde68a',
                      borderRadius: 8,
                      fontSize: 12,
                      color: '#92400e',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8
                    }}
                  >
                    <FaBan size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                      <strong>Legal Hold Active:</strong> All behavioral telemetry data retention is strictly locked. Telemetry purge is blocked until legal hold is disabled.
                    </span>
                  </div>
                )}

                <div
                  style={{
                    padding: '12px 14px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: '#475569'
                  }}
                >
                  <p style={{ margin: '0 0 6px 0', fontWeight: 600, color: '#1e293b' }}>
                    What data is deleted?
                  </p>
                  <p style={{ margin: '0 0 8px 0' }}>
                    Click density grids, daily interaction metrics, attention/dwell intervals, customer analytics interactions, sessions, and usage counters are permanently wiped.
                  </p>
                  <p style={{ margin: 0, color: '#15803d', fontWeight: 600 }}>
                    ✓ Site collection metadata, user flows, and allowed origins remain 100% intact.
                  </p>
                </div>

                {/* PURGE OPTIONS */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      cursor: 'pointer',
                      padding: '10px 12px',
                      background: purgeOptions.includeSnapshots ? '#fff7ed' : '#ffffff',
                      border: '1px solid',
                      borderColor: purgeOptions.includeSnapshots ? '#fdba74' : '#e2e8f0',
                      borderRadius: 8,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <input
                      type="checkbox"
                      data-testid="purge-include-snapshots-checkbox"
                      checked={purgeOptions.includeSnapshots}
                      onChange={(e) =>
                        setPurgeOptions((prev) => ({ ...prev, includeSnapshots: e.target.checked }))
                      }
                      style={{ marginTop: 3, accentColor: '#ea580c', width: 16, height: 16 }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                        Purge DOM HTML/CSS Page Snapshots
                      </span>
                      <span style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
                        {purgeOptions.includeSnapshots
                          ? 'Captured DOM layouts will be purged along with click grids.'
                          : 'Retain page layouts and DOM snapshots; only delete click interactions and metrics.'}
                      </span>
                    </div>
                  </label>

                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      cursor: 'pointer',
                      padding: '10px 12px',
                      background: purgeOptions.includeSettings ? '#fef2f2' : '#ffffff',
                      border: '1px solid',
                      borderColor: purgeOptions.includeSettings ? '#fca5a5' : '#e2e8f0',
                      borderRadius: 8,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <input
                      type="checkbox"
                      data-testid="purge-include-settings-checkbox"
                      checked={purgeOptions.includeSettings}
                      onChange={(e) =>
                        setPurgeOptions((prev) => ({ ...prev, includeSettings: e.target.checked }))
                      }
                      style={{ marginTop: 3, accentColor: '#dc2626', width: 16, height: 16 }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                        Reset Page Definitions & Capture Settings
                      </span>
                      <span style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
                        {purgeOptions.includeSettings
                          ? 'Reset registered page definitions and capture configuration back to defaults.'
                          : 'Keep registered application pages and custom settings preserved.'}
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* MODAL FOOTER */}
              <div
                style={{
                  padding: '14px 24px',
                  background: '#f8fafc',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 12
                }}
              >
                <button
                  type="button"
                  data-testid="cancel-purge-telemetry-btn"
                  onClick={handleClosePurgeModal}
                  disabled={Boolean(purgingTelemetryId)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="confirm-purge-telemetry-btn"
                  onClick={handleExecutePurge}
                  disabled={
                    Boolean(purgingTelemetryId) ||
                    Boolean(siteContainers[purgeTarget?.siteId]?.legalHoldActive)
                  }
                  style={{
                    padding: '8px 18px',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 700,
                    border: 'none',
                    background: siteContainers[purgeTarget?.siteId]?.legalHoldActive ? '#94a3b8' : '#dc2626',
                    color: '#ffffff',
                    cursor: siteContainers[purgeTarget?.siteId]?.legalHoldActive ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: siteContainers[purgeTarget?.siteId]?.legalHoldActive ? 0.7 : 1
                  }}
                >
                  {purgingTelemetryId ? (
                    <>
                      <FaSyncAlt className="spin" size={12} /> Purging Telemetry...
                    </>
                  ) : (
                    <>
                      <FaTrash size={12} /> Purge Telemetry Data
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {pendingOperation && (
          <DomainPolicyRecovery key={`${pendingOperation.organizationId}:${pendingOperation.operationId}`}
            operation={pendingOperation} token={token} onResolved={site => {
              if (pendingOperation.resumeEditor) {
                setEditingId(site.id);
                setSavedDomainConfigurations(getDomainConfigurations(site));
                setUncertainCreate(false);
                setInspectedSite({ id: site.id, organizationId: site.organizationId });
              }
              if (pendingOperation.isBlockingOperation === true) {
                setSaveError('Blocking operation completed. Your preserved draft was not submitted; review and submit it now.');
                setUncertainCreate(false);
                if (pendingOperation.blockedAction) setConfirmation(pendingOperation.blockedAction);
                showToast.warning('Blocking operation completed. Your requested change has not been applied.');
              } else {
                setSaveError('');
                showToast.success('Original operation completed. Retained draft edits have not been submitted.');
              }
              setPendingOperation(null);
              fetchSiteCollections(pendingOperation.organizationId);
              setPolicyRefresh(value => value + 1);
            }} />
        )}



        {/* MAIN TABLE */}
        <div ref={tableRef} style={tableCardStyle}>
          <div style={toolbarStyle}>
            <div style={searchWrapper}>
              <FaSearch style={searchIcon} />
              <input
                style={searchInput}
                placeholder="Search collections by name, domain, or ID..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
            <div>
              {canCreateOrUpdateCollections && (
                <button
                  disabled={Boolean(pendingOperation) || submitting}
                  onClick={() => {
                    if (showForm) {
                      handleCloseForm();
                    } else {
                      setShowForm(true);
                      setEditingId(null);
                      setInspectedSite(null);
                      resetForm(isSystemAdmin ? selectedOrgId : orgId);
                    }
                  }}
                  style={primaryButtonStyle}
                >
                  {showForm ? <FaTimes /> : <FaPlus />} {showForm ? 'Close' : 'Create New'}
                </button>
              )}
            </div>
          </div>

          {blockingState ? (
            <div style={blockingStateStyle}>
              <div style={blockingTitleStyle}>{blockingState.title}</div>
              <div style={blockingMessageStyle}>{blockingState.message}</div>
            </div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr style={tableHeaderRowStyle}>
                  <th style={thStyle}>Collection Information</th>
                  <th style={thStyle}>Registry Status</th>
                  <th style={thActionStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="3" style={emptyStateStyle}><FaSyncAlt className="spin" /> Syncing...</td></tr>
                ) : paginatedItems.length === 0 ? (
                  <tr><td colSpan="3" style={emptyStateStyle}>No collections found.</td></tr>
                ) : (
                  paginatedItems.map((sc) => {
                    const hasConflict = hasInactiveOwners(sc);
                    const isExpanded = expandedId === sc.id;
                    const isConfirming = confirmation?.id === sc.id;

                    const domains = Array.from(new Set((sc.domainNames || []).map(normalizeDomain).filter(Boolean)));
                    const primaryDomain = domains[0] || '-';
                    const extraCount = Math.max(0, domains.length - 1);

                    return (
                      <React.Fragment key={sc.id}>
                        <tr style={{ ...trStyle, background: isExpanded ? '#f8fafc' : 'white' }}>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{ ...avatarStyle, background: hasConflict ? '#ef4444' : '#6366f1' }}>{sc.name?.[0]}</div>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div style={userNameText}>{sc.name}</div>
                                  {hasConflict && (
                                    <span style={inactiveWarningBadge} title="Warning: Inactive owners detected">
                                      <FaExclamationTriangle size={10} /> Inactive Owner
                                    </span>
                                  )}
                                </div>

                                <div style={userEmailText}>
                                  {primaryDomain}
                                  {extraCount > 0 ? ` (+${extraCount} more)` : ''}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td style={tdStyle}>
                            <span style={getBadgeStyle(sc.isActive ? 'Active' : 'Inactive')}>
                              <FaCircle size={6} /> {sc.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>

                          <td style={tdActionStyle}>
                            {isConfirming ? (
                              <div style={inlineConfirmWrapper}>
                                <FaQuestionCircle color="#6366f1" size={14} />
                                <span style={confirmText}>Confirm {confirmation.type === 'TOGGLE_STATUS' ? 'Status' : 'Delete'}?</span>
                                <button style={btnYes} disabled={Boolean(pendingOperation) || submitting} onClick={executeAction}>Yes</button>
                                <button style={btnNo} onClick={() => setConfirmation(null)}>No</button>
                              </div>
                            ) : (
                              <div style={actionButtonGroupStyle}>
                                <button
                                  style={{ ...actionBtnBase, background: '#f1f5f9', color: '#64748b' }}
                                  title="View Details"
                                  onClick={() => setExpandedId(isExpanded ? null : sc.id)}
                                >
                                  <FaChevronDown style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                                </button>

                                {canCreateOrUpdateCollections && (
                                  <button
                                    style={actionBtnView}
                                    title="Edit"
                                    disabled={Boolean(pendingOperation) || submitting}
                                    onClick={() => {
                                      const uniqueDomains = Array.from(new Set((sc.domainNames || []).map(normalizeDomain).filter(Boolean)));
                                      const cachedPolicy = siteReplayPolicies[sc.id];

                                      setEditingId(sc.id);
                                      setInspectedSite({ id: sc.id, organizationId: sc.organizationId || getTargetOrganizationId() });
                                      setForm({
                                        name: sc.name || '',
                                        description: sc.description || '',
                                        domainNames: uniqueDomains,
                                        isActive: Boolean(sc.isActive),
                                        organizationId: sc.organizationId || getTargetOrganizationId(),
                                        previewBaseUrl: sc.previewBaseUrl ?? '',
                                        domainConfigurations: Array.isArray(sc.domainConfigurations)
                                          ? getDomainConfigurations(sc, cachedPolicy)
                                          : sc.domainConfigurations
                                      });

                                      if (sc.containerName || sc.storageContainer) {
                                        const cName = sc.containerName || sc.storageContainer?.containerName;
                                        const cStatus = sc.containerStatus || sc.storageContainer?.status || 'ready';
                                        setSiteContainers(prev => ({
                                          ...prev,
                                          [sc.id]: {
                                            ...(prev[sc.id] || {}),
                                            containerName: cName,
                                            status: cStatus,
                                            legalHoldActive: Boolean(sc.legalHoldActive ?? sc.storageContainer?.legalHoldActive ?? prev[sc.id]?.legalHoldActive)
                                          }
                                        }));
                                      }

                                      setDomainToAdd('');
                                      setSavedDomainConfigurations(getDomainConfigurations(sc, cachedPolicy));
                                      setSaveError('');
                                      setUncertainCreate(false);
                                      setShowForm(true);
                                      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                                    }}
                                  >
                                    <FaEdit size={12} />
                                  </button>
                                )}

                                {canCreateOrUpdateCollections && (
                                  <button
                                    style={sc.isActive ? actionBtnToggleActive : actionBtnToggleInactive}
                                    title={sc.isActive ? "Set Inactive" : "Set Active"}
                                    disabled={Boolean(pendingOperation) || submitting}
                                    onClick={() => setConfirmation({ id: sc.id, type: 'TOGGLE_STATUS', data: sc })}
                                  >
                                    {sc.isActive ? <FaToggleOn size={16} /> : <FaToggleOff size={16} />}
                                  </button>
                                )}

                                {canDeleteCollections && (
                                  <button
                                    style={actionBtnDelete}
                                    title="Delete"
                                    disabled={Boolean(pendingOperation) || submitting}
                                    onClick={() => setConfirmation({ id: sc.id, type: 'DELETE' })}
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>

                        {isExpanded && !isConfirming && (
                          <tr style={{ background: '#f8fafc' }}>
                            <td colSpan="3" style={{ padding: '12px 24px', borderBottom: '1px solid #f1f5f9' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <span style={labelStyle}>SiteCollection ID: {sc.id}</span>
                                <span style={labelStyle}>PreviewBaseUrl: {sc.previewBaseUrl || 'Not configured'}</span>
                                <span style={labelStyle}>Domain Names</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                  {domains.length === 0 ? (
                                    <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>No domains</span>
                                  ) : (
                                    domains.map((d) => (
                                      <span key={d} style={domainBadgeStyle}>
                                        {d}
                                      </span>
                                    ))
                                  )}
                                </div>

                                <span style={labelStyle}>Assigned Owners Detail</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                  {Array.from(new Set(sc.owners || [])).map(oid => {
                                    const owner = getOwnerDetails(oid);
                                    return (
                                      <div key={oid} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                        <FaCircle size={6} color={owner.isActive ? '#10b981' : '#ef4444'} />
                                        <span style={{ fontSize: 13, fontWeight: 600, color: owner.isActive ? '#1e293b' : '#ef4444' }}>
                                          {owner.firstName} {owner.lastName} {!owner.isActive && '(Inactive)'}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>

                                <span style={labelStyle}>Storage Container Readiness</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
                                  <span style={{ color: isContainerReady(sc.id) ? '#10b981' : '#64748b' }}>
                                    {siteContainers[sc.id]?.containerName
                                      ? `Container: ${siteContainers[sc.id].containerName} (${(siteContainers[sc.id].status || 'ready').toUpperCase()})`
                                      : isContainerReady(sc.id)
                                        ? 'Container: Provisioned & Ready'
                                        : 'Blob Container: Default managed or pending provisioning'}
                                  </span>
                                  {canCreateOrUpdateCollections && !isContainerReady(sc.id) && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleProvisionContainer(sc.id, sc.domainNames, sc.organizationId || organizationSelection);
                                      }}
                                      disabled={provisioningContainerId === sc.id}
                                      style={{ ...primaryButtonStyle, padding: '4px 10px', fontSize: 12 }}
                                    >
                                      {provisioningContainerId === sc.id ? <FaSyncAlt className="spin" size={10} /> : 'Provision Container'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          )}

          {/* PAGINATION */}
          {!blockingState && filteredCollections.length > itemsPerPage && (
            <div style={paginationRow}>
              <div style={paginationInfoStyle}>Showing <b>{paginatedItems.length}</b> collections</div>
              <div style={paginationControlsWrapper}>
                <button style={pageArrow} disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><FaChevronLeft /></button>
                <button style={pageArrow} disabled={currentPage === Math.ceil(filteredCollections.length / itemsPerPage)} onClick={() => setCurrentPage(p => p + 1)}><FaChevronRight /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .spin { animation: spin 2s infinite linear; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .kpi-card { transition: all 0.4s ease; cursor: pointer; }
        .kpi-card:hover { transform: translateY(-5px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1) !important; }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @media (max-width: 768px) {
          .site-collection-drawer-panel {
            max-width: 100% !important;
            width: 100% !important;
          }
          .site-collection-form-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

/* ---------- STYLES ---------- */
const drawerBackdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.55)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  zIndex: 1050,
  display: 'flex',
  justifyContent: 'flex-end',
  animation: 'fadeIn 0.2s ease-out'
};

const drawerPanelStyle = {
  width: '100%',
  maxWidth: 900,
  height: '100vh',
  maxHeight: '100vh',
  background: '#ffffff',
  boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.16)',
  display: 'flex',
  flexDirection: 'column',
  animation: 'slideInRight 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
  position: 'relative',
  zIndex: 1051
};

const drawerHeaderStyle = {
  padding: '18px 24px',
  borderBottom: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#ffffff',
  position: 'sticky',
  top: 0,
  zIndex: 10
};

const drawerHeaderIconBadge = {
  width: 40,
  height: 40,
  borderRadius: 12,
  background: '#f5f3ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#6366f1'
};

const drawerHeadingStyle = {
  fontSize: 18,
  fontWeight: 800,
  color: '#0f172a',
  margin: 0
};

const drawerSubheadingStyle = {
  fontSize: 12,
  color: '#64748b',
  margin: '2px 0 0 0'
};

const drawerCloseBtnStyle = {
  background: '#f1f5f9',
  border: 'none',
  borderRadius: 8,
  width: 34,
  height: 34,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#64748b',
  transition: 'all 0.15s ease'
};

const drawerBodyStyle = {
  padding: '24px',
  overflowY: 'auto',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 20
};

const drawerFormGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 24
};

const drawerFooterStyle = {
  padding: '16px 24px',
  borderTop: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 12,
  background: '#f8fafc',
  position: 'sticky',
  bottom: 0,
  zIndex: 10
};
const dropdownContainerStyle = { position: 'relative', width: '100%' };
const dropdownTriggerStyle = { width: '100%', padding: '10px 14px', borderRadius: 10, borderWidth: '1.5px', borderStyle: 'solid', borderColor: '#e2e8f0', fontSize: 14, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontWeight: 500, color: '#1e293b', transition: 'all 0.2s ease', outline: 'none' };
const dropdownTriggerTextStyle = { flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const dropdownMenuStyle = { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, marginTop: 4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', zIndex: 1000, overflow: 'hidden', animation: 'fadeIn 0.2s ease-out' };
const searchBoxContainerStyle = { position: 'relative', padding: '8px 12px', borderBottom: '1px solid #f1f5f9', background: '#fcfdfe' };
const searchIconStyle = { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12, pointerEvents: 'none' };
const dropdownSearchInputStyle = { width: '100%', padding: '8px 12px 8px 28px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff' };
const dropdownListStyle = { maxHeight: '200px', overflowY: 'auto', padding: '4px' };
const dropdownOptionStyle = { width: '100%', padding: '10px 12px', border: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 6, margin: '2px 0', transition: 'all 0.2s ease', fontSize: 13 };
const optionTextStyle = { flex: 1 };
const optionNameStyle = { fontWeight: 600, color: 'inherit' };
const optionIdStyle = { fontSize: '11px', opacity: 0.6, marginTop: 2 };
const emptyOptionStyle = { padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 13 };

const wrapperStyle = { width: '100%', minHeight: '100vh', background: '#f8fafc', paddingBottom: 60, fontFamily: 'Inter, sans-serif' };
const containerStyle = { maxWidth: 1150, margin: '0 auto', padding: '24px', animation: 'fadeIn 0.5s ease-out' };
const headerStyle = { marginBottom: 32 };
const headerContentStyle = { display: 'flex', alignItems: 'center', gap: 16 };
const headerBadgeStyle = { width: 48, height: 48, borderRadius: 14, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1', boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.1)' };
const titleStyle = { fontSize: 24, fontWeight: 800, margin: 0, color: '#0f172a' };
const subtitleStyle = { fontSize: 14, color: '#64748b', marginTop: 4 };

const kpiRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 32 };
const kpiCardBase = { padding: 24, borderRadius: 24, border: '1px solid #e2e8f0', background: '#fff' };
const kpiCardWhite = { ...kpiCardBase, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' };
const kpiCardActiveAll = { ...kpiCardBase, border: '2px solid #6366f1', background: '#f5f3ff' };
const kpiCardActiveActive = { ...kpiCardBase, border: '2px solid #10b981', background: '#f0fdf4' };
const kpiCardActiveInactive = { ...kpiCardBase, border: '2px solid #ef4444', background: '#fef2f2' };
const kpiHeader = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 };
const kpiIcon = { width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const kpiLabel = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#64748b' };
const kpiValue = { fontSize: 32, fontWeight: 800, color: '#1e293b' };
const kpiMeta = { fontSize: 11, color: '#94a3b8' };

const filtersCardStyle = { background: '#fff', borderRadius: 16, padding: 24, marginBottom: 24, border: '1px solid #e2e8f0' };
const cardHeaderStyle = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 12, borderBottom: '1px solid #f1f5f9' };
const cardTitleStyle = { fontSize: 15, fontWeight: 700, color: '#1e293b' };
const filtersGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, alignItems: 'end' };
const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' };
const selectStyle = { padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14, color: '#1e293b', background: '#fff', outline: 'none' };
const primaryButtonStyle = { background: '#6366f1', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', fontSize: 14 };

const tableCardStyle = { background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' };
const toolbarStyle = { padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fcfdfe' };
const searchWrapper = { position: 'relative', width: '300px' };
const searchIcon = { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };

const searchInput = {
  width: '100%',
  padding: '9px 12px 9px 38px',
  borderRadius: 10,
  border: '1.5px solid #e2e8f0',
  fontSize: 13,
  outline: 'none',
  background: '#fff',
  color: '#1e293b'
};

const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const tableHeaderRowStyle = { background: '#f8fafc' };
const thStyle = { padding: '16px 20px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'left' };
const thActionStyle = { ...thStyle, textAlign: 'right' };
const trStyle = { borderBottom: '1px solid #f1f5f9' };
const tdStyle = { padding: '16px 20px', verticalAlign: 'middle' };
const tdActionStyle = { padding: '16px 20px', textAlign: 'right' };

const avatarStyle = { width: 38, height: 38, borderRadius: 10, background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 };
const userNameText = { fontWeight: 700, color: '#1e293b', fontSize: 14 };
const userEmailText = { fontSize: 12, color: '#64748b', marginTop: 2 };

const inactiveWarningBadge = { background: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800', display: 'inline-flex', alignItems: 'center', gap: '4px', border: '1px solid #fecaca' };

const domainBadgeStyle = { background: 'rgba(2, 132, 199, 0.08)', border: '1px solid rgba(2, 132, 199, 0.25)', padding: '6px 12px', borderRadius: 100, fontSize: 12, fontWeight: 700, color: '#0369a1', display: 'flex', alignItems: 'center' };

const paginationRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: '#fff', borderTop: '1px solid #f1f5f9' };
const paginationInfoStyle = { fontSize: 14, color: '#64748b' };
const paginationControlsWrapper = { display: 'flex', alignItems: 'center', gap: 8 };
const pageArrow = { minWidth: 36, height: 36, borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' };

const actionButtonGroupStyle = { display: 'flex', gap: 8, justifyContent: 'flex-end' };
const actionBtnBase = { width: 34, height: 34, borderRadius: 9, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const actionBtnView = { ...actionBtnBase, background: '#ede9fe', color: '#6366f1' };
const actionBtnToggleActive = { ...actionBtnBase, background: '#dcfce7', color: '#166534' };
const actionBtnToggleInactive = { ...actionBtnBase, background: '#fee2e2', color: '#991b1b' };
const actionBtnDelete = { ...actionBtnBase, background: '#f1f5f9', color: '#64748b' };

const inlineConfirmWrapper = { display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end', width: '100%' };
const confirmText = { fontSize: '12px', fontWeight: '700', color: '#475569' };
const btnYes = { background: '#6366f1', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' };
const btnNo = { background: '#fff', color: '#64748b', border: '1.5px solid #e2e8f0', padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' };

const emptyStateStyle = { padding: 60, textAlign: 'center', color: '#94a3b8', fontSize: 14 };
const blockingStateStyle = { padding: 48, textAlign: 'center', borderTop: '1px solid #f1f5f9' };
const blockingTitleStyle = { fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 8 };
const blockingMessageStyle = { fontSize: 14, color: '#64748b' };

const getBadgeStyle = (status) => {
  const base = { padding: '4px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, textTransform: 'uppercase' };
  return status === 'Active' ? { ...base, background: '#dcfce7', color: '#166534' } : { ...base, background: '#fee2e2', color: '#991b1b' };
};